<script lang="ts">
  import { tweened } from 'svelte/motion';
  import { cubicOut } from 'svelte/easing';

  // Inputs
  export let maxXss: number = 0;   // Total XSS
  export let maxXhss: number = 0;  // High XSS (XHSS)
  export let maxXpss: number = 0;  // Peak XSS (XPSS)
  export let revealed: boolean = false;
  export let useMobileLayout: boolean;

  // Display tweened values for count-up
  const totalDisplay = tweened(0, { duration: 800, easing: cubicOut });
  const highDisplay = tweened(0, { duration: 850, easing: cubicOut });
  const peakDisplay = tweened(0, { duration: 900, easing: cubicOut });

  // Start/stop animations when revealed changes
  $: if (revealed) {
    totalDisplay.set(maxXss ?? 0);
    highDisplay.set(maxXhss ?? 0);
    peakDisplay.set(maxXpss ?? 0);
  } else {
    totalDisplay.set(0);
    highDisplay.set(0);
    peakDisplay.set(0);
  }

  // Ensure Svelte recognizes props as used (silence unused export warnings)
  $: void maxXss;
  $: void maxXhss;
  $: void maxXpss;
  $: void revealed;

  const tidy = (n: number) => new Intl.NumberFormat().format(Math.round(n ?? 0));

  const bulbs = Array.from({ length: 18 });
</script>

<div class="marquee-card {revealed ? 'revealed' : ''} {useMobileLayout ? 'mobile' : 'desktop'}" aria-label="Strain Showcase">
  <!-- Header marquee with twinkling bulbs -->
  <div class="marquee-header" aria-hidden="true">
    <div class="bulbs top">
      {#each bulbs as _, i}
        <span class="bulb" style="--d:{i * 0.08}s"></span>
      {/each}
    </div>
    <div class="title">XSS Records</div>
    <div class="bulbs bottom">
      {#each bulbs as _, i}
        <span class="bulb" style="--d:{i * 0.08}s"></span>
      {/each}
    </div>
  </div>

  <!-- Triptych posters -->
  <div class="posters">
    <section class="poster total" aria-label="Total XSS">
      <div class="poster-head">Total XSS</div>
      <div class="number">
        <span class="value">{tidy($totalDisplay)}</span><span class="unit"> XSS</span>
      </div>
      <div class="underline" aria-hidden="true"></div>
      <div class="subtitle">Highest in a single activity</div>
    </section>

    <section class="poster high" aria-label="High XSS">
      <div class="poster-head">High XSS</div>
      <div class="number">
        <span class="value">{tidy($highDisplay)}</span><span class="unit"> XSS</span>
      </div>
      <div class="underline" aria-hidden="true"></div>
      <div class="subtitle">Highest in a single activity</div>
    </section>

    <section class="poster peak" aria-label="Peak XSS">
      <div class="poster-head">Peak XSS</div>
      <div class="number">
        <span class="value">{tidy($peakDisplay)}</span><span class="unit"> XSS</span>
      </div>
      <div class="underline" aria-hidden="true"></div>
      <div class="subtitle">Highest in a single activity</div>
    </section>
  </div>

  <!-- Decorative footer -->
  <div class="footer" aria-hidden="true">
    <div class="footlights">
      {#each Array.from({ length: 12 }) as _, i}
        <span class="footlight" style="--d:{i * 0.1}s"></span>
      {/each}
    </div>
  </div>
</div>

<style>
  .marquee-card {
    background: radial-gradient(120% 90% at 50% 0%, rgba(10, 14, 24, 0.95), rgba(6, 8, 14, 1)),
                linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.02));
    border-radius: 16px;
    position: relative;
    overflow: hidden;
    color: #e9edf5;
    /* Elevated shadow + subtle brand glow to stand out on dark backgrounds */
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.35), 0 0 30px rgba(94, 230, 232, 0.2);
  }
  /* Card sizing per layout */
  .mobile.marquee-card { width: 300px; max-height: 500px; }
  .desktop.marquee-card { width: 600px; max-height: 1000px; }
  /* subtle vignette + grain */
  .marquee-card::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(120% 80% at 50% 100%, rgba(0,0,0,0.4), rgba(0,0,0,0) 60%),
                repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 2px, rgba(0,0,0,0.02) 4px, rgba(0,0,0,0.02) 6px);
    mix-blend-mode: soft-light;
  }

  .marquee-header {
    padding: 10px 12px 8px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    background: linear-gradient(180deg, rgba(18, 24, 38, 0.9), rgba(12, 18, 30, 0.9));
  }
  .marquee-header .title {
    font-weight: 700;
    letter-spacing: 0.4px;
    text-align: center;
    font-size: 14px;
    color: #f4f7fb;
    text-shadow: 0 1px 0 rgba(255,255,255,0.06);
  }
  .desktop .marquee-header { padding: 16px 18px 12px; }
  .desktop .marquee-header .title { font-size: 20px; letter-spacing: 0.6px; }
  .bulbs {
    display: grid;
    grid-template-columns: repeat(18, 1fr);
    gap: 4px;
    margin-bottom: 6px;
  }
  .bulbs.bottom { margin-top: 6px; margin-bottom: 0; }
  .bulb {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: radial-gradient(circle at 50% 30%, #ffd89b, #d6a85d);
    box-shadow: 0 0 6px rgba(255, 216, 155, 0.8);
    opacity: 0.65;
    animation: twinkle 2.6s ease-in-out infinite;
    animation-delay: var(--d, 0s);
  }
  .desktop .bulb { width: 12px; height: 12px; box-shadow: 0 0 8px rgba(255,216,155,0.9); }
  @keyframes twinkle {
    0%, 100% { opacity: 0.55; filter: brightness(0.95); }
    50% { opacity: 0.9; filter: brightness(1.15); }
  }

  .posters {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 12px;
  }
  /* Mobile: stack posters vertically */
  .mobile .posters { grid-template-columns: 1fr; }
  .desktop .posters { gap: 14px; padding: 18px; }

  .poster {
    position: relative;
    background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02));
    border-radius: 10px;
    padding: 10px 8px 12px;
    border: 1px solid rgba(255,255,255,0.08);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 6px 12px rgba(0,0,0,0.25);
    opacity: 0;
    transform: translateY(12px);
  }
  .desktop .poster { padding: 18px 14px 20px; border-radius: 12px; box-shadow: inset 0 1px 0 rgba(255,255,255,0.08), 0 10px 18px rgba(0,0,0,0.3); }
  /* Mobile poster reflow: head left, value right */
  .mobile .poster { display: grid; grid-template-columns: 1fr auto; grid-auto-rows: auto; align-items: center; }
  .mobile .poster .poster-head { grid-column: 1; grid-row: 1; margin-bottom: 0; }
  .mobile .poster .number { grid-column: 2; grid-row: 1; justify-content: flex-end; margin-bottom: 0; }
  .mobile .poster .underline { grid-column: 1 / -1; grid-row: 2; margin-top: 6px; }
  .mobile .poster .subtitle { grid-column: 1 / -1; grid-row: 3; margin-top: 6px; }
  .poster::after { /* accent flicker overlay */
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 10px;
    box-shadow: 0 0 0 1px rgba(var(--accent-rgb, 255,255,255), 0.2), 0 0 12px rgba(var(--accent-rgb, 255,255,255), 0.15);
    opacity: 0;
  }
  .revealed .poster {
    animation: slideUp 520ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
  }
  .revealed .poster.total { animation-delay: 60ms; }
  .revealed .poster.high { animation-delay: 140ms; }
  .revealed .poster.peak { animation-delay: 220ms; }
  .revealed .poster::after {
    animation: flicker 600ms ease-in-out 1 forwards;
  }
  .revealed .poster.total::after { animation-delay: 260ms; }
  .revealed .poster.high::after { animation-delay: 340ms; }
  .revealed .poster.peak::after { animation-delay: 420ms; }

  @keyframes slideUp {
    0% { opacity: 0; transform: translateY(12px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes flicker {
    0% { opacity: 0; filter: brightness(1); }
    25% { opacity: 0.6; filter: brightness(1.15) contrast(1.05); }
    50% { opacity: 0.3; filter: brightness(0.95); }
    75% { opacity: 0.7; filter: brightness(1.1); }
    100% { opacity: 0; filter: brightness(1); }
  }

  .poster-head {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: none;
    margin-bottom: 8px;
    color: rgba(235, 240, 249, 0.92);
  }
  .desktop .poster-head { font-size: 14px; font-weight: 700; text-align: center; }

  .number {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 4px;
    font-weight: 800;
    color: #f7fafc;
    text-shadow: 0 1px 0 rgba(255,255,255,0.05);
    margin-bottom: 6px;
  }
  .number .value { font-size: 24px; letter-spacing: -0.3px; }
  .number .unit { font-size: 12px; color: rgba(235,240,249,0.8); }
  .desktop .number .value { font-size: 36px; letter-spacing: -0.4px; }
  .desktop .number .unit { font-size: 16px; }
  .mobile .number { justify-content: flex-end; }

  .underline {
    height: 2px;
    background: rgb(var(--accent-rgb, 200,200,200));
    border-radius: 2px;
    transform: scaleX(0);
    transform-origin: left;
    opacity: 0.85;
  }
  .desktop .underline { height: 3px; }
  .revealed .poster.total .underline { animation: drawLine 700ms cubicOut 1 forwards 320ms; }
  .revealed .poster.high .underline { animation: drawLine 700ms cubicOut 1 forwards 400ms; }
  .revealed .poster.peak .underline { animation: drawLine 700ms cubicOut 1 forwards 480ms; }
  @keyframes drawLine { to { transform: scaleX(1); } }

  .subtitle {
    margin-top: 6px;
    font-size: 10px;
    line-height: 1.2;
    color: rgba(235,240,249,0.75);
    text-align: center;
  }
  .desktop .subtitle { font-size: 12px; }

  /* Poster accent themes */
  .poster.total { --accent: #0B5DBC; --accent-rgb: 11, 93, 188; }
  .poster.high  { --accent: #F5A623; --accent-rgb: 245, 166, 35; }
  .poster.peak  { --accent: #FF0000; --accent-rgb: 255, 0, 0; }
  .poster.total .poster-head { color: rgba(173, 200, 240, 0.95); }
  .poster.high .poster-head  { color: rgba(251, 209, 139, 0.95); }
  .poster.peak .poster-head  { color: rgba(255, 170, 170, 0.95); }
  .poster.total .value { color: rgb(173, 200, 240); }
  .poster.high .value  { color: rgb(251, 209, 139); }
  .poster.peak .value  { color: rgb(255, 170, 170); }
  .poster.total .unit { color: rgb(173, 200, 240); }
  .poster.high .unit  { color: rgb(251, 209, 139); }
  .poster.peak .unit  { color: rgb(255, 170, 170); }
  .poster.total { box-shadow: inset 0 0 0 1px rgba(11,93,188,0.4), 0 6px 12px rgba(11,93,188,0.3); }
  .poster.high  { box-shadow: inset 0 0 0 1px rgba(245,166,35,0.4), 0 6px 12px rgba(245,166,35,0.3); }
  .poster.peak  { box-shadow: inset 0 0 0 1px rgba(255,0,0,0.4), 0 6px 12px rgba(255,0,0,0.3); }

  /* Decorative footer */
  .footer {
    padding: 8px 12px 12px;
    border-top: 1px solid rgba(255,255,255,0.08);
    background: linear-gradient(180deg, rgba(12, 16, 26, 0.9), rgba(8, 12, 20, 0.95));
  }
  .footlights {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 6px;
  }
  .footlight {
    height: 6px;
    border-radius: 3px;
    background: radial-gradient(circle at 50% 50%, #b0c6ff, #6e85c7);
    box-shadow: 0 0 8px rgba(130, 160, 255, 0.6);
    opacity: 0.45;
    animation: pulse 2.2s ease-in-out infinite;
    animation-delay: var(--d, 0s);
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.35; }
    50% { opacity: 0.7; }
  }
</style>
